import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import {
  IBannerCampaign,
  IRequestBannerEvent,
  IRequestPointEarn,
  IRequestPointRedeem,
  IResponseBranch,
  IResponseBranchProduct,
  IResponseBranchUserInterface,
  IResponseGeneral,
  IResponsePayment,
  IResponseVerifyPayment,
  IResponsePaymentType,
  IResponsePointBalance,
  IResponsePointEarn,
  IResponsePointRedeem,
  IResponsePromotion,
  IResponseReason,
  IResponseSession,
  IResponseActiveBanners,
  IResponseSuccess,
  IResponseBypassReasons,
  IResponseBypassRequest,
  IResponsePinLockout,
  IResponsePinUse,
  IResponseStaffPinCheck,
  StaffMenuAction,
  StaffPinPurpose,
} from 'src/app/shared/interfaces/interface';
import { AppConfig, StorageKey } from 'src/app/shared/constants/global-config.constant';
import { environment } from 'src/environments/environment';
import { getHttpRetryOperator } from 'src/app/shared/utils/http-retry.util';
import { CommonService } from '../common/common.service';

type NewType = IResponseSuccess;

@Injectable({
  providedIn: 'root'
})
export class DataService {

  constructor(
    private httpClient: HttpClient,
    private commonService: CommonService
  ) { }

  getAppAllowedGuardHeaders(): HttpHeaders {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'x-app-env': environment.x_app_env,
      'x-app-domain': environment.x_app_domain,
      'x-app-id': environment.x_app_id,
      'x-app-secret': environment.x_app_secret,
    })
    return headers;
  }

  getAppGuardHeaders(): HttpHeaders {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem(StorageKey.SESSION_ID)}`,
    })
    return headers;
  }

  getVersion(): Observable<IResponseSuccess> {
    return this.httpClient.get<IResponseSuccess>(
      `${environment.apiEndpoint}/core/version`,
      { headers: this.getAppAllowedGuardHeaders() }
    );
  }

  createAppSession(payload: any): Observable<IResponseSession> {
    return this.httpClient.post<IResponseSession>(
      `${environment.apiEndpoint}/auth/app/session`,
      payload,
      { headers: this.getAppAllowedGuardHeaders() }
    ).pipe(
      getHttpRetryOperator({
        onRetry: (current, max) => this.commonService.showRetryToast(current, max)
      })
    );
  }

  getQrVerification(branch_code, qr): Observable<IResponseSuccess> {
    return this.httpClient.get<IResponseSuccess>(
      `${environment.apiEndpoint}/auth/verify/qr/${branch_code}/${qr}`,
      { headers: this.getAppGuardHeaders() }
    );
  }

  getReasonList(): Observable<IResponseReason> {
    return this.httpClient.get<IResponseReason>(
      `${environment.apiEndpoint}/application/reason`,
      { headers: this.getAppGuardHeaders() }
    );
  }

  getBranchByCode(branch_code): Observable<IResponseBranch> {
    return this.httpClient.get<IResponseBranch>(
      `${environment.apiEndpoint}/application/branch/code/${branch_code}`,
      { headers: this.getAppAllowedGuardHeaders() }
    );
  }

  getBranchById(id): Observable<IResponseBranch> {
    return this.httpClient.get<IResponseBranch>(
      `${environment.apiEndpoint}/application/branch/id/${id}`,
      { headers: this.getAppAllowedGuardHeaders() }
    );
  }

  getBranchProductByCode(): Observable<IResponseBranchProduct> {
    let branch_code = localStorage.getItem(StorageKey.BRANCH_CODE);
    return this.httpClient.get<IResponseBranchProduct>(
      `${environment.apiEndpoint}/application/branch/products/code/${branch_code}`,
      { headers: this.getAppAllowedGuardHeaders() }
    ).pipe(
      getHttpRetryOperator({
        skipRetryOnStatusCodes: [400, 401, 403, 404],
        onRetry: (current, max) => this.commonService.showRetryToast(current, max)
      })
    );
  }

  getBranchInterfaceById(id): Observable<IResponseBranchUserInterface> {
    return this.httpClient.get<IResponseBranchUserInterface>(
      `${environment.apiEndpoint}/application/branch/interface/id/${id}`,
      { headers: this.getAppAllowedGuardHeaders() }
    ).pipe(
      getHttpRetryOperator({
        skipRetryOnStatusCodes: [400, 401, 403, 404],
        onRetry: (current, max) => this.commonService.showRetryToast(current, max)
      })
    );
  }

  /**
   * @param silent Suppresses the retry toast. Required for background polling: the toast is
   *   a Swal, and Swal.fire tears down whatever popup is already open — a poll retrying
   *   while the customer is looking at the back-confirmation dialog would close it for them.
   */
  getPaymentInfo(trx_id: string, silent: boolean = false): Observable<IResponsePayment> {
    return this.httpClient.get<IResponsePayment>(
      `${environment.apiEndpoint}/application/payment/${trx_id}`,
      { headers: this.getAppGuardHeaders() }
    ).pipe(
      getHttpRetryOperator({
        skipRetryOnStatusCodes: [400, 401, 403, 404],
        onRetry: silent ? undefined : (current, max) => this.commonService.showRetryToast(current, max)
      })
    );
  }

  /**
   * Asks the API to check the payment with Omise directly (and record it if paid), for the
   * CONFIRM button. Deliberately no retry operator: the server already falls back to the DB
   * when Omise is slow, and one tap should give one quick answer — the caller shows a popup
   * on failure and the customer can tap again.
   */
  verifyPayment(trx_id: string): Observable<IResponseVerifyPayment> {
    return this.httpClient.post<IResponseVerifyPayment>(
      `${environment.apiEndpoint}/application/payment/${trx_id}/verify`,
      {},
      { headers: this.getAppGuardHeaders() }
    );
  }

  getCreatePayment(payload): Observable<IResponsePayment> {
    return this.httpClient.post<IResponsePayment>(
      `${environment.apiEndpoint}/application/payment`,
      payload,
      { headers: this.getAppGuardHeaders() }
    ).pipe(
      getHttpRetryOperator({
        skipRetryOnStatusCodes: [400, 401, 403, 404],
        onRetry: (current, max) => this.commonService.showRetryToast(current, max)
      })
    );
  }

  getPromotion(code: string): Observable<IResponsePromotion> {
    return this.httpClient.get<IResponsePromotion>(
      `${environment.apiEndpoint}/promotions/code/${code}?branch_id=${localStorage.getItem(StorageKey.BRANCH_ID)}`,
      { headers: this.getAppGuardHeaders() }
    ).pipe(
      getHttpRetryOperator({
        skipRetryOnStatusCodes: [400, 401, 403, 404],
        onRetry: (current, max) => this.commonService.showRetryToast(current, max)
      })
    );
  }

  updatePromotionByCode(code: string, payload: any) {
    return this.httpClient.patch<IResponseGeneral>(
      `${environment.apiEndpoint}/promotions/code/${code}`, payload,
      { headers: this.getAppGuardHeaders() }
    );
  }

  updatePromotionById(id: string, payload: any) {
    return this.httpClient.patch<IResponseGeneral>(
      `${environment.apiEndpoint}/promotions/id/${id}`, payload,
      { headers: this.getAppGuardHeaders() }
    ).pipe(
      getHttpRetryOperator({
        skipRetryOnStatusCodes: [400, 401, 403, 404],
        onRetry: (current, max) => this.commonService.showRetryToast(current, max)
      })
    );
  }

  updatePaymentByTrxId(trx_id: string, payload: any) {
    return this.httpClient.patch<IResponsePayment>(
      `${environment.apiEndpoint}/application/payment/${trx_id}`, payload,
      { headers: this.getAppGuardHeaders() }
    ).pipe(
      getHttpRetryOperator({
        delayMs: 5000,
        skipRetryOnStatusCodes: [400, 401, 403, 404],
        onRetry: (current, max) => this.commonService.showRetryToast(current, max)
      })
    );
  }

  getPaymentType() {
    return this.httpClient.get<IResponsePaymentType[]>(
      `${environment.apiEndpoint}/application/payment/types`,
      { headers: this.getAppGuardHeaders() }
    ).pipe(
      getHttpRetryOperator({
        skipRetryOnStatusCodes: [400, 401, 403, 404],
        onRetry: (current, max) => this.commonService.showRetryToast(current, max)
      })
    );
  }

  getPointBalance(phone_no: string) {
    return this.httpClient.get<IResponsePointBalance>(
      `${environment.apiEndpoint}/points/${phone_no}`,
      { headers: this.getAppGuardHeaders() }
    );
  }

  earnPoint(payload: IRequestPointEarn): Observable<IResponsePointEarn> {
    return this.httpClient.post<IResponsePointEarn>(
      `${environment.apiEndpoint}/points`,
      payload,
      { headers: this.getAppGuardHeaders() }
    );
  }

  redeemPoint(payload: IRequestPointRedeem): Observable<IResponsePointRedeem> {
    return this.httpClient.post<IResponsePointRedeem>(
      `${environment.apiEndpoint}/points/redemption`,
      payload,
      { headers: this.getAppGuardHeaders() }
    )
  }


  /**
   * Banner campaigns live right now for this branch, for the /home popup. Deliberately no
   * retry operator and no toast: this is a background request fired on every /home entry, and
   * an advertisement that fails to load must never show a Swal or delay the customer. A slow
   * answer is as good as none - the caller only opens the popup if it is still on /home.
   */
  getActiveBanners(branch_id: string): Observable<IResponseActiveBanners> {
    return this.httpClient.get<IResponseActiveBanners>(
      `${environment.apiEndpoint}/banners/active/branch/${branch_id}`,
      { headers: this.getAppGuardHeaders() }
    ).pipe(
      timeout(AppConfig.BANNER_FETCH_TIMEOUT_MS)
    );
  }

  /** Fire-and-forget banner event (shown / slide_view / closed); no retry, caller ignores errors. */
  sendBannerEvent(payload: IRequestBannerEvent): Observable<IResponseSuccess> {
    return this.httpClient.post<IResponseSuccess>(
      `${environment.apiEndpoint}/banners/events`,
      payload,
      { headers: this.getAppGuardHeaders() }
    );
  }

  // ---- Staff PIN payment bypass and approval (contract §2) ----
  // Deliberately no retry operator and no retry toast on any of these: a PIN must never be
  // sent twice by a retry (a second POST /bypass would record a second bypass, and every
  // wrong PIN counts toward the lockout), and the dialog shows its own error and lets the
  // staff member try again. The PIN only ever travels in the JSON body (D24).

  checkStaffPin(pin: string, purpose: StaffPinPurpose): Observable<IResponseStaffPinCheck> {
    return this.httpClient.post<IResponseStaffPinCheck>(
      `${environment.apiEndpoint}/bypass/pin/check`,
      { pin, purpose },
      { headers: this.getAppGuardHeaders() }
    );
  }

  /** Grants a payment bypass within the limit (or for Global staff, or with approval off). */
  createBypass(pin: string, reason_id: number): Observable<IResponsePinUse> {
    return this.httpClient.post<IResponsePinUse>(
      `${environment.apiEndpoint}/bypass`,
      { pin, reason_id },
      { headers: this.getAppGuardHeaders() }
    );
  }

  /** Sends an approval request; the answer is PENDING or NO_APPROVER. */
  createBypassRequest(pin: string, reason_id: number): Observable<IResponseBypassRequest> {
    return this.httpClient.post<IResponseBypassRequest>(
      `${environment.apiEndpoint}/bypass/requests`,
      { pin, reason_id },
      { headers: this.getAppGuardHeaders() }
    );
  }

  /** The polling fallback for an approval request. */
  getBypassRequest(id: number): Observable<IResponseBypassRequest> {
    return this.httpClient.get<IResponseBypassRequest>(
      `${environment.apiEndpoint}/bypass/requests/${id}`,
      { headers: this.getAppGuardHeaders() }
    );
  }

  getBypassRequestEventsUrl(id: number): string {
    return `${environment.apiEndpoint}/bypass/requests/${id}/events`;
  }

  /**
   * Cancels an approval request, fire-and-forget (D35). A keepalive fetch rather than HttpClient
   * so the request still goes out when it is sent from ngOnDestroy on the way off the page.
   * Idempotent on the server; a lost cancel is covered by the server's 5-minute expiry.
   */
  cancelBypassRequest(id: number): void {
    try {
      this.fetchFn(`${environment.apiEndpoint}/bypass/requests/${id}/cancel`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem(StorageKey.SESSION_ID)}`,
        },
        body: '{}',
      }).catch(err => console.warn(`[bypass] cancel of request ${id} failed`, err));
    }
    catch (err) {
      // Fire-and-forget: the server expires the request anyway. Logged so a device check can
      // tell whether the cancel left the photobooth.
      console.warn(`[bypass] cancel of request ${id} failed`, err);
    }
  }

  /** Records a staff-menu entry (D27). No reason and no limit. */
  useStaffMenu(pin: string, action: StaffMenuAction): Observable<IResponsePinUse> {
    return this.httpClient.post<IResponsePinUse>(
      `${environment.apiEndpoint}/staff-menu`,
      { pin, action },
      { headers: this.getAppGuardHeaders() }
    );
  }

  /** Active reasons, sorted by sort_order. */
  getBypassReasons(): Observable<IResponseBypassReasons> {
    return this.httpClient.get<IResponseBypassReasons>(
      `${environment.apiEndpoint}/bypass/reasons`,
      { headers: this.getAppGuardHeaders() }
    );
  }

  /**
   * Whether this app session's PIN lockout is still running (D53). Read only: it records nothing
   * and never counts toward the lockout. Asked every few seconds while the lockout countdown is on
   * screen, so no retry and no toast (the next check is the retry). A check that has not answered
   * within STAFF_PIN_LOCKOUT_CHECK_TIMEOUT_MS is dropped, before the next one is due.
   */
  getPinLockout(): Observable<IResponsePinLockout> {
    return this.httpClient.get<IResponsePinLockout>(
      `${environment.apiEndpoint}/bypass/pin/lockout`,
      { headers: this.getAppGuardHeaders() }
    ).pipe(timeout(AppConfig.STAFF_PIN_LOCKOUT_CHECK_TIMEOUT_MS));
  }

  /** Overridable in tests. */
  fetchFn: typeof fetch = (input, init) => fetch(input, init);
}
